import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ShieldAlert, ShieldCheck, Plus, X, AlertTriangle, CheckCircle2, Lock, MapPin, ArrowRight, Search } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import { api, formatError } from '../lib/api';
import { useAuth } from '../context/AuthContext';

/**
 * /risk-trust — Redesigned Client Conduct Log (Risk & Trust module)
 * LEGALLY SAFE version:
 * - Verified job (address+date+quote) required
 * - Objective enum categories only (no free text)
 * - Balanced entries (positive signals mandatory)
 * - Evidence required for negative signals
 * - Client auto-notified · 14-day dispute window
 * - Auto-expire 24 months
 * - Access: Tradies only, invisible to consumers
 */
export default function RiskTrustModule() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [showLookup, setShowLookup] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login?redirect=/risk-trust');
      return;
    }
    if (user.role !== 'tradie' && user.role !== 'admin') {
      toast.error('The Risk & Trust module is exclusively for trade professionals.');
      navigate('/');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.pnListMyRiskTrust();
      setEntries(r.data || []);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="risk-trust-page">
      <Header />
      <div className="mx-auto max-w-6xl px-6 py-10 md:px-10">
        {/* Header */}
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#D96C4E]">
              <Lock size={12} /> Trade Professionals Only · Private
            </div>
            <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight text-[#1C1B1A] md:text-5xl">Risk &amp; Trust</h1>
            <p className="mt-3 max-w-2xl text-sm text-[#5C5B5A]">
              A factual conduct record for trade professionals. Verified jobs · objective signals · evidence-required · dispute-protected. This module is never visible to consumers.
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowLookup(true)} className="btn-ghost" data-testid="risk-trust-lookup-btn">
              <Search size={14} /> Check address
            </button>
            <button onClick={() => setShowNew(true)} className="btn-primary" data-testid="risk-trust-new-btn">
              <Plus size={14} /> New entry
            </button>
          </div>
        </div>

        {/* Safety banner */}
        <div className="mt-8 border border-[#1A362D] bg-[#1A362D]/[0.04] p-5" data-testid="risk-trust-safety-banner">
          <div className="flex items-start gap-3">
            <ShieldCheck size={20} className="mt-0.5 flex-shrink-0 text-[#1A362D]" />
            <div className="text-sm text-[#1C1B1A]">
              <div className="font-semibold">This is a factual record — not a review or opinion.</div>
              <div className="mt-1 text-[#5C5B5A]">
                Every entry requires a verified job (address · date · quote), objective category selection, evidence attached, and includes a mandatory positive signal for balance. The client is auto-notified and has 14 days to dispute. False or malicious entries may result in account termination and personal legal liability.
              </div>
            </div>
          </div>
        </div>

        {/* Entries list */}
        <section className="mt-10">
          <div className="mb-4 flex items-end justify-between">
            <h2 className="font-heading text-2xl font-bold text-[#1C1B1A]">My entries</h2>
            <div className="text-xs text-[#8A8988]">{entries.length} entr{entries.length === 1 ? 'y' : 'ies'}</div>
          </div>

          {loading ? (
            <div className="border border-dashed border-[#E5E4E0] bg-white p-10 text-center text-sm text-[#8A8988]" data-testid="risk-trust-loading">Loading…</div>
          ) : entries.length === 0 ? (
            <div className="border border-dashed border-[#E5E4E0] bg-white p-12 text-center" data-testid="risk-trust-empty">
              <Shield size={32} className="mx-auto text-[#8A8988]" />
              <h3 className="mt-4 font-heading text-xl font-bold text-[#1C1B1A]">No entries yet</h3>
              <p className="mt-2 text-sm text-[#5C5B5A]">Log your first client conduct entry — factual, evidence-based, and dispute-protected.</p>
              <button onClick={() => setShowNew(true)} className="btn-primary mt-6 inline-flex" data-testid="risk-trust-empty-new-btn">
                <Plus size={14} /> Create first entry
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {entries.map((e) => <EntryCard key={e.id} entry={e} />)}
            </div>
          )}
        </section>
      </div>

      {showNew && <NewEntryModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      {showLookup && <LookupModal onClose={() => setShowLookup(false)} />}
    </div>
  );
}

function EntryCard({ entry }) {
  const totalNeg = entry.payment_signals.length + entry.scope_signals.length + entry.onsite_signals.length;
  const totalPos = entry.positive_signals.length;
  const badge = entry.status === 'disputed' ? { text: 'Under dispute', tone: 'warn' } :
                entry.status === 'removed' ? { text: 'Removed', tone: 'muted' } :
                totalNeg > totalPos ? { text: 'Risk signals', tone: 'red' } :
                totalPos > totalNeg ? { text: 'Positive record', tone: 'green' } :
                { text: 'Neutral record', tone: 'muted' };

  return (
    <div className="border border-[#E5E4E0] bg-white p-5" data-testid={`entry-card-${entry.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="font-heading text-base font-bold text-[#1C1B1A]" data-testid={`entry-client-name-${entry.id}`}>{entry.client_display_name}</div>
            <BadgePill tone={badge.tone} text={badge.text} />
          </div>
          <div className="mt-1 flex items-center gap-1 text-xs text-[#8A8988]">
            <MapPin size={11} /> {entry.postcode} · Job date {new Date(entry.job_date).toLocaleDateString('en-AU')}
          </div>
        </div>
        <div className="text-right text-xs text-[#8A8988]">
          <div>Logged {new Date(entry.created_at).toLocaleDateString('en-AU')}</div>
          <div>Expires {new Date(entry.expires_at).toLocaleDateString('en-AU')}</div>
        </div>
      </div>

      {entry.positive_signals.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {entry.positive_signals.map((s, i) => (
            <span key={i} className="inline-flex items-center gap-1 border border-[#1A362D] bg-[#1A362D]/[0.06] px-2 py-0.5 text-[10px] font-medium text-[#1A362D]">
              <CheckCircle2 size={11} /> {s}
            </span>
          ))}
        </div>
      )}
      {(entry.payment_signals.length + entry.scope_signals.length + entry.onsite_signals.length) > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {[...entry.payment_signals, ...entry.scope_signals, ...entry.onsite_signals].map((s, i) => (
            <span key={i} className="inline-flex items-center gap-1 border border-[#D96C4E] bg-[#D96C4E]/[0.08] px-2 py-0.5 text-[10px] font-medium text-[#D96C4E]">
              <AlertTriangle size={11} /> {s}
            </span>
          ))}
        </div>
      )}

      <div className="mt-4 flex items-center justify-between border-t border-[#E5E4E0] pt-3 text-xs text-[#5C5B5A]">
        <span>{entry.has_evidence ? '✓ Evidence attached' : '⚠ No evidence — recommend adding'}</span>
        <span>Dispute deadline: {new Date(entry.dispute_deadline).toLocaleDateString('en-AU')}</span>
      </div>
    </div>
  );
}

function BadgePill({ tone, text }) {
  const cls = tone === 'green' ? 'border-[#1A362D] bg-[#1A362D]/[0.08] text-[#1A362D]'
            : tone === 'red' ? 'border-[#D96C4E] bg-[#D96C4E]/[0.08] text-[#D96C4E]'
            : tone === 'warn' ? 'border-amber-600 bg-amber-50 text-amber-800'
            : 'border-[#E5E4E0] bg-[#F3F2EE] text-[#5C5B5A]';
  return (
    <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] ${cls}`}>{text}</span>
  );
}

function LookupModal({ onClose }) {
  const [form, setForm] = useState({ address_line: '', suburb: '', postcode: '' });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const doLookup = async (e) => {
    e.preventDefault();
    setBusy(true);
    setResult(null);
    try {
      const r = await api.pnRiskTrustLookup(form);
      setResult(r.data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="risk-trust-lookup-modal">
      <div className="w-full max-w-lg border border-[#1C1B1A] bg-white">
        <div className="flex items-center justify-between border-b border-[#E5E4E0] px-6 py-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#D96C4E]">Risk & Trust</div>
            <h3 className="font-heading text-xl font-bold text-[#1C1B1A]">Check an address before you quote</h3>
          </div>
          <button onClick={onClose} className="text-[#8A8988] hover:text-[#1C1B1A]" data-testid="close-lookup"><X size={18} /></button>
        </div>
        <form onSubmit={doLookup} className="space-y-3 p-6">
          <input value={form.address_line} onChange={(e) => setForm({ ...form, address_line: e.target.value })} placeholder="Street address" className="input-base" data-testid="lookup-address" required />
          <div className="grid grid-cols-2 gap-3">
            <input value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })} placeholder="Suburb" className="input-base" data-testid="lookup-suburb" required />
            <input value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} placeholder="Postcode" maxLength={4} className="input-base" data-testid="lookup-postcode" required />
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="lookup-submit">
            {busy ? 'Checking…' : 'Check address'} <Search size={14} />
          </button>
        </form>
        {result && (
          <div className="border-t border-[#E5E4E0] bg-[#FAF9F6] p-6" data-testid="lookup-result">
            {!result.match ? (
              <div className="flex items-start gap-3">
                <ShieldCheck size={20} className="mt-0.5 text-[#1A362D]" />
                <div>
                  <div className="font-semibold text-[#1C1B1A]">No factual records at this address.</div>
                  <div className="mt-1 text-xs text-[#5C5B5A]">Clean history from Trades &amp; Neighbours peer network. Proceed with your normal due diligence.</div>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <ShieldAlert size={20} className={`mt-0.5 ${result.trust_level === 'low' ? 'text-[#D96C4E]' : result.trust_level === 'high' ? 'text-[#1A362D]' : 'text-amber-600'}`} />
                <div>
                  <div className="font-semibold text-[#1C1B1A]">{result.entry_count} record{result.entry_count === 1 ? '' : 's'} found · trust: {result.trust_level}</div>
                  <div className="mt-1 text-xs text-[#5C5B5A]">Positive signals: {result.positive_signal_count} · Risk signals: {result.negative_signal_count}</div>
                  <div className="mt-1 text-xs text-[#5C5B5A]">Latest entry: {new Date(result.latest_entry_date).toLocaleDateString('en-AU')}</div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function NewEntryModal({ onClose, onCreated }) {
  const [step, setStep] = useState(1); // 1 = terms, 2 = form
  const [cats, setCats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    address_line: '', suburb: '', postcode: '',
    client_first_name: '', client_surname_initial: '',
    client_phone_last4: '',
    job_date: new Date().toISOString().slice(0, 10),
    quote_amount_aud: '',
    invoice_number: '',
    positive_signals: [], payment_signals: [], scope_signals: [], onsite_signals: [],
    evidence_filename: '', evidence_mime_type: '', evidence_data_b64: '',
    police_event_number: '',
    confirmed_factual: false,
  });

  useEffect(() => {
    api.pnRiskTrustCategories().then((r) => setCats(r.data)).catch(() => {});
  }, []);

  const toggleSig = (bucket, id) => {
    setForm((f) => {
      const cur = new Set(f[bucket]);
      if (cur.has(id)) cur.delete(id); else cur.add(id);
      return { ...f, [bucket]: [...cur] };
    });
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Evidence file must be under 5MB.');
      return;
    }
    const r = new FileReader();
    r.onload = () => {
      const raw = String(r.result);
      const b64 = raw.split(',')[1] || '';
      setForm((f) => ({ ...f, evidence_filename: file.name, evidence_mime_type: file.type, evidence_data_b64: b64 }));
      toast.success('Evidence attached.');
    };
    r.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.confirmed_factual) {
      toast.error('You must confirm the entry is factual before submitting.');
      return;
    }
    setBusy(true);
    try {
      await api.pnCreateRiskTrust({
        ...form,
        quote_amount_aud: form.quote_amount_aud ? Number(form.quote_amount_aud) : undefined,
        client_phone_last4: form.client_phone_last4 || undefined,
        invoice_number: form.invoice_number || undefined,
        evidence_filename: form.evidence_filename || undefined,
        evidence_mime_type: form.evidence_mime_type || undefined,
        evidence_data_b64: form.evidence_data_b64 || undefined,
        police_event_number: form.police_event_number || undefined,
      });
      toast.success('Entry logged. Client will be notified and has 14 days to dispute.');
      onCreated();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  if (step === 1) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 p-4" data-testid="risk-trust-terms-modal">
        <div className="w-full max-w-2xl border border-[#1C1B1A] bg-white">
          <div className="flex items-center justify-between border-b border-[#E5E4E0] px-6 py-4">
            <h3 className="font-heading text-xl font-bold text-[#1C1B1A]">Before you continue</h3>
            <button onClick={onClose} className="text-[#8A8988] hover:text-[#1C1B1A]" data-testid="close-terms"><X size={18} /></button>
          </div>
          <div className="p-6 text-sm text-[#1C1B1A]">
            <p>The Risk &amp; Trust log is a <strong>factual record</strong> of professional interactions between trade businesses and their clients. It is <strong>not</strong> for personal opinions, gossip, or emotional statements.</p>
            <p className="mt-4">By submitting this entry, you confirm that:</p>
            <ul className="mt-3 list-decimal space-y-2 pl-6 text-[#5C5B5A]">
              <li>The information you provide is <strong>factual</strong> and based on your direct professional interaction with the client at the address specified.</li>
              <li>You have <strong>evidence</strong> to support any negative signals you select (attached to this entry).</li>
              <li>You understand the client will be <strong>automatically notified</strong> and has <strong>14 days to dispute</strong>.</li>
              <li>You understand that <strong>false, malicious, or unsupported entries</strong> may result in account termination and <strong>personal legal liability</strong>.</li>
              <li>You accept that Trades &amp; Neighbours acts as a <strong>conduit</strong> — responsibility for accuracy sits with you.</li>
            </ul>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={onClose} className="btn-ghost" data-testid="terms-cancel">Cancel</button>
              <button onClick={() => setStep(2)} className="btn-primary" data-testid="terms-accept">
                I confirm — continue <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const hasNegative = form.payment_signals.length + form.scope_signals.length + form.onsite_signals.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 pt-6" data-testid="risk-trust-form-modal">
      <div className="w-full max-w-3xl border border-[#1C1B1A] bg-white">
        <div className="flex items-center justify-between border-b border-[#E5E4E0] px-6 py-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#D96C4E]">Risk & Trust · New entry</div>
            <h3 className="font-heading text-xl font-bold text-[#1C1B1A]">Log a factual client conduct record</h3>
          </div>
          <button onClick={onClose} className="text-[#8A8988] hover:text-[#1C1B1A]" data-testid="close-new-entry"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-6 p-6">
          {/* Layer 1: verified job */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#1A362D]">Step 1 · Verified job</div>
            <h4 className="mt-1 font-heading text-base font-bold text-[#1C1B1A]">Where and when</h4>
            <div className="mt-3 grid grid-cols-1 gap-3">
              <input value={form.address_line} onChange={(e) => setForm({ ...form, address_line: e.target.value })} placeholder="Job address" className="input-base" data-testid="entry-address" required />
              <div className="grid grid-cols-2 gap-3">
                <input value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })} placeholder="Suburb" className="input-base" data-testid="entry-suburb" required />
                <input value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} placeholder="Postcode" maxLength={4} className="input-base" data-testid="entry-postcode" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <input value={form.client_first_name} onChange={(e) => setForm({ ...form, client_first_name: e.target.value })} placeholder="Client first name" className="input-base" data-testid="entry-client-first" required />
                  <div className="mt-1 text-[10px] text-[#8A8988]">Displayed as "John S. (Fremantle)" — never full name.</div>
                </div>
                <input value={form.client_surname_initial} onChange={(e) => setForm({ ...form, client_surname_initial: e.target.value })} placeholder="Surname initial" maxLength={2} className="input-base" data-testid="entry-client-initial" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input type="date" value={form.job_date} onChange={(e) => setForm({ ...form, job_date: e.target.value })} className="input-base" data-testid="entry-job-date" required />
                <input value={form.quote_amount_aud} onChange={(e) => setForm({ ...form, quote_amount_aud: e.target.value })} placeholder="Quote amount (AUD)" type="number" step="0.01" className="input-base" data-testid="entry-quote-amount" />
              </div>
              <input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} placeholder="Invoice / job number (optional)" className="input-base" data-testid="entry-invoice-number" />
            </div>
          </div>

          {/* Layer 2: objective signals */}
          {cats && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#1A362D]">Step 2 · Objective signals</div>
              <h4 className="mt-1 font-heading text-base font-bold text-[#1C1B1A]">Select only what applies (factual)</h4>

              <SignalGroup title="Positive signals (at least one required)" bucket="positive_signals" items={cats.positive} form={form} onToggle={toggleSig} tone="green" />
              <SignalGroup title="Payment conduct" bucket="payment_signals" items={cats.payment} form={form} onToggle={toggleSig} tone="red" />
              <SignalGroup title="Quote &amp; scope" bucket="scope_signals" items={cats.scope} form={form} onToggle={toggleSig} tone="red" />
              <SignalGroup title="On-site conduct" bucket="onsite_signals" items={cats.onsite} form={form} onToggle={toggleSig} tone="red" />
            </div>
          )}

          {/* Layer 3: evidence */}
          {hasNegative && (
            <div className="border border-amber-300 bg-amber-50 p-4" data-testid="evidence-required-block">
              <div className="flex items-start gap-2">
                <AlertTriangle size={18} className="mt-0.5 flex-shrink-0 text-amber-600" />
                <div className="text-sm">
                  <div className="font-semibold text-amber-900">Evidence is required for any negative signal.</div>
                  <div className="mt-1 text-xs text-amber-800">Attach a screenshot, invoice, email PDF, or SMS log. Max 5MB.</div>
                  <label className="mt-3 inline-flex cursor-pointer items-center gap-2 border border-amber-600 bg-white px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-amber-800 hover:bg-amber-100" data-testid="evidence-upload-label">
                    <input type="file" onChange={handleFile} className="hidden" accept="image/*,application/pdf" data-testid="entry-evidence-file" />
                    {form.evidence_filename ? `✓ ${form.evidence_filename}` : 'Attach evidence'}
                  </label>
                  {form.onsite_signals.includes('verbal_aggression_with_police_ref') && (
                    <input value={form.police_event_number} onChange={(e) => setForm({ ...form, police_event_number: e.target.value })} placeholder="Police event number (required for aggression flag)" className="input-base mt-3" data-testid="entry-police-ref" required />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Layer 4: confirmation */}
          <label className="flex cursor-pointer items-start gap-3 border border-[#1A362D] bg-[#1A362D]/[0.05] p-4" data-testid="entry-confirm-label">
            <input type="checkbox" checked={form.confirmed_factual} onChange={(e) => setForm({ ...form, confirmed_factual: e.target.checked })} className="mt-1" data-testid="entry-confirm-checkbox" />
            <div className="text-sm text-[#1C1B1A]">
              I confirm this record is <strong>factual</strong> and based on my direct professional interaction. I understand the client will be notified, has 14 days to dispute, and that <strong>false or malicious entries may result in personal legal liability</strong>.
            </div>
          </label>

          <div className="flex justify-end gap-2 border-t border-[#E5E4E0] pt-4">
            <button type="button" onClick={onClose} className="btn-ghost" data-testid="entry-cancel">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="entry-submit">
              {busy ? 'Submitting…' : 'Submit entry'} <ArrowRight size={14} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SignalGroup({ title, bucket, items, form, onToggle, tone }) {
  const cls = tone === 'green'
    ? 'border-[#1A362D] bg-[#1A362D]/[0.06] text-[#1A362D]'
    : 'border-[#D96C4E] bg-[#D96C4E]/[0.06] text-[#D96C4E]';
  const activeCls = tone === 'green'
    ? 'border-[#1A362D] bg-[#1A362D] text-white'
    : 'border-[#D96C4E] bg-[#D96C4E] text-white';
  return (
    <div className="mt-4">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#5C5B5A]">{title}</div>
      <div className="mt-2 flex flex-wrap gap-2">
        {items.map((it) => {
          const active = form[bucket].includes(it.id);
          return (
            <button
              type="button"
              key={it.id}
              onClick={() => onToggle(bucket, it.id)}
              className={`border px-3 py-1.5 text-xs font-medium ${active ? activeCls : cls}`}
              data-testid={`signal-${bucket}-${it.id}`}
            >
              {it.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
